import { useEffect, useState } from 'react';
import { useFeature } from '../../lib/features';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ExternalLink } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { Button } from '../../components/ui/Button';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { Toast } from '../../components/ui/Toast';
import { ACCOUNT_TIER_LABEL, type AccountTier, type BillingInterval, type TierCatalogEntry } from '../../types';

const SALES_EMAIL = 'support@zmist.co';

function formatLimit(n: number): string {
  if (n < 0) return 'Unlimited';
  return n.toLocaleString();
}

function priceLabel(entry: TierCatalogEntry, interval: BillingInterval): string {
  const amount = interval === 'yearly' ? entry.priceYearly : entry.priceMonthly;
  if (amount < 0) return 'Custom';
  if (amount === 0) return 'Free';
  return interval === 'yearly' ? `$${amount.toLocaleString()}/yr` : `$${amount}/mo`;
}

export function PlanScreen() {
  const navigate = useNavigate();
  const checkoutOn = useFeature('billing.checkout');
  const { profile, tierCatalog, billing, refreshBilling, startUpgrade, openBillingPortalPage } = useStore();
  const [params, setParams] = useSearchParams();
  const [interval, setInterval] = useState<BillingInterval>('monthly');
  const [busyTier, setBusyTier] = useState<AccountTier | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const currentTier = billing?.tier.id ?? profile.tier;

  // Returning from Stripe Checkout (BILLING_SUCCESS_URL / _CANCEL_URL point
  // back here with ?checkout=success|cancel).
  useEffect(() => {
    const outcome = params.get('checkout');
    if (!outcome) return;
    if (outcome === 'success') {
      setNotice('Payment received - your plan is being updated.');
      refreshBilling();
    } else if (outcome === 'cancel') {
      setNotice('Checkout cancelled - your plan is unchanged.');
    }
    params.delete('checkout');
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tiers = tierCatalog;

  const onUpgrade = async (entry: TierCatalogEntry) => {
    setError('');
    if (entry.cta === 'contact') {
      window.location.href = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(`Zmist ${entry.name} plan enquiry`)}`;
      return;
    }
    setBusyTier(entry.id);
    try {
      await startUpgrade(entry.id, interval);
      // startUpgrade redirects the browser; nothing runs after this on success.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout.');
      setBusyTier(null);
    }
  };

  const onManageBilling = async () => {
    setError('');
    setPortalBusy(true);
    try {
      await openBillingPortalPage();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open the billing portal.');
    } finally {
      setPortalBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <ScreenHeader title="Plan" />

      <FormSection title="Current plan">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-lg font-bold">{ACCOUNT_TIER_LABEL[currentTier] ?? currentTier}</span>
            {billing?.tier.status && billing.tier.status !== 'active' && (
              <span className="text-xs text-amber-600 dark:text-amber-400 capitalize">{billing.tier.status}</span>
            )}
            {billing?.tier.currentPeriodEnd && (
              <span className="text-xs text-black/45 dark:text-white/45">
                {billing.tier.cancelAtPeriodEnd ? 'Ends' : 'Renews'}{' '}
                {new Date(billing.tier.currentPeriodEnd).toLocaleDateString()}
              </span>
            )}
          </div>
          {currentTier !== 'free' && checkoutOn && (
            <Button variant="bordered" onClick={onManageBilling} loading={portalBusy}>
              Manage billing
            </Button>
          )}
        </div>

        {billing && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-black/55 dark:text-white/55">Channels</span>
              <span className="font-medium tabular-nums">
                {billing.usage.ownedChannels} / {formatLimit(billing.entitlements.limits.activeChannels)}
              </span>
            </div>
            {billing.entitlements.limits.activeChannels > 0 && (
              <div className="h-1.5 rounded-full bg-black/[0.08] dark:bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-blue-600"
                  style={{
                    width: `${Math.min(
                      100,
                      (billing.usage.ownedChannels / billing.entitlements.limits.activeChannels) * 100
                    )}%`,
                  }}
                />
              </div>
            )}
          </div>
        )}
      </FormSection>

      {tiers.length > 0 && (
        <FormSection title="Compare plans">
          <SegmentedControl
            value={interval}
            onChange={(v) => setInterval(v as BillingInterval)}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'yearly', label: 'Yearly' },
            ]}
          />

          <div className="flex flex-col gap-3">
            {tiers.map((entry) => {
              const isCurrent = entry.id === currentTier;
              return (
                <div
                  key={entry.id}
                  className={`rounded-xl border p-4 flex flex-col gap-2 ${
                    isCurrent
                      ? 'border-blue-500/60 bg-blue-500/[0.06]'
                      : 'border-black/[0.08] dark:border-white/[0.10]'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-bold">{entry.name}</span>
                    <span className="text-sm font-medium tabular-nums">{priceLabel(entry, interval)}</span>
                  </div>
                  <p className="text-xs text-black/50 dark:text-white/50 m-0">{entry.tagline}</p>
                  <ul className="text-xs text-black/60 dark:text-white/60 flex flex-col gap-1 m-0 pl-0 list-none">
                    <li className="flex gap-1.5">
                      <Check size={14} className="shrink-0 mt-0.5 text-blue-600" />
                      {formatLimit(entry.limits.activeChannels)} active channels
                    </li>
                    <li className="flex gap-1.5">
                      <Check size={14} className="shrink-0 mt-0.5 text-blue-600" />
                      {formatLimit(entry.limits.storedSignalsPerMonth)} signals / month
                    </li>
                    <li className="flex gap-1.5">
                      <Check size={14} className="shrink-0 mt-0.5 text-blue-600" />
                      {entry.limits.retentionDays < 0
                        ? 'Unlimited history'
                        : `${entry.limits.retentionDays}-day history`}
                    </li>
                  </ul>

                  {isCurrent ? (
                    <Button variant="secondary" full disabled>
                      Current plan
                    </Button>
                  ) : !checkoutOn ? null : entry.cta === 'contact' ? (
                    <Button variant="bordered" full onClick={() => onUpgrade(entry)}>
                      Contact sales <ExternalLink size={14} />
                    </Button>
                  ) : entry.cta === 'checkout' ? (
                    <Button
                      variant="primary"
                      full
                      onClick={() => onUpgrade(entry)}
                      loading={busyTier === entry.id}
                    >
                      {busyTier === entry.id ? 'Starting checkout' : `Upgrade to ${entry.name}`}
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-black/40 dark:text-white/40 m-0">
            Payments are handled securely by Stripe. You can change or cancel anytime from “Manage billing”.
          </p>
        </FormSection>
      )}

      {tiers.length === 0 && (
        <button
          type="button"
          onClick={() => navigate('/settings')}
          className="text-sm text-black/45 dark:text-white/45 text-left"
        >
          Pricing is unavailable right now.
        </button>
      )}

      {error && <Toast message={error} onDismiss={() => setError('')} />}
      {notice && !error && <Toast message={notice} onDismiss={() => setNotice('')} />}
    </div>
  );
}
